import { InfoCircleFilled, ClockCircleOutlined, MailOutlined } from '@ant-design/icons';
import { faUniversalAccess, faArrowAltCircleDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, Space, Tabs, Col, Row, Tooltip, Typography, type ColProps } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type TFunction, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import LoadingElement from '../../../components/LoadingElement';
import { useDashboardContext } from './DashboardContext';
import useApplicationCounterData, { type ApplicationCounter } from './data/useApplicationCounterData';
import useActiveTabPane from './shared/useActiveTabPane';
import useSeriesFields from './shared/useSeriesField';

export const StatisticsViewID = 'statistics';

const { Title, Text } = Typography;

const StyledRow = styled(Row)`
    @media (min-width: 992px) {
        .ant-col-lg-4 {
            display: block;
            flex: 0 0 20%;
            max-width: 20%;
        }
    }
`;

const StyledCard = styled(Card)`
    background: ${({ theme }) => theme.dashboardTheme.statistics.backgroundColor};
    color: ${({ theme }) => theme.dashboardTheme.statistics.color};
    padding: 0;
    border-radius: 0.25rem;
    height: 9rem;

    h2 {
        color: ${({ theme }) => theme.dashboardTheme.statistics.color};
        margin: 0 0 6.5px;
    }
    & .ant-card-body {
        padding: 24px;
        font-size: 13px;
    }

    & .ant-typography-secondary {
        color: ${({ theme }) => theme.dashboardTheme.statistics.color};
    }
`;

const StyledInfoCircleFilled = styled(InfoCircleFilled)`
    font-size: 24px;
    position: absolute;
    top: 10px;
    right: 10px;
    cursor: pointer;
`;

const StyledClockCircleOutlined = styled(ClockCircleOutlined)`
    font-size: 24px;
`;

const StyledMailOutlined = styled(MailOutlined)`
    font-size: 24px;
`;

const StyledText = styled(Text)`
    display: block;
`;

const BgIcon = styled(FontAwesomeIcon)`
    display: inline-block;
    font-size: 5.71rem;
    position: absolute;
    z-index: 1;
    bottom: -1rem;
    right: -1rem;
    opacity: 0.4;
`;

type StatisticCardProps = {
    value: string;
    subTitle: string;
    tooltip?: string;
    iconType: 'mail' | 'clock';
};
const StatisticCard = ({ value, subTitle, tooltip, iconType }: StatisticCardProps) => (
    <StyledCard>
        {tooltip && (
            <Tooltip title={tooltip}>
                <StyledInfoCircleFilled />
            </Tooltip>
        )}

        <Space>
            {iconType === 'mail' ? <StyledMailOutlined /> : <StyledClockCircleOutlined color="green" />}
            <Title level={2}>{value}</Title>
        </Space>
        <StyledText type="secondary">{subTitle}</StyledText>
        <BgIcon icon={iconType === 'mail' ? faUniversalAccess : faArrowAltCircleDown} />
    </StyledCard>
);

type StatisticsBlockProps = {
    noOfSubmitted: number;
    noOfApproved: number;
    submittedTillApprovedOrDeclinedTime: string;
    customerInfoReceivedTillApprovedOrDeclinedTime: string;
    dbsInstantApprovalStats?: {
        total: number;
        instantApproved: number;
        rate: number;
    };
    t: TFunction;
};
const StatisticsBlock = ({
    noOfSubmitted,
    noOfApproved,
    submittedTillApprovedOrDeclinedTime,
    customerInfoReceivedTillApprovedOrDeclinedTime,
    dbsInstantApprovalStats,
    t,
}: StatisticsBlockProps) => {
    const company = useCompany(true);

    const colSpan: Partial<ColProps> = useMemo(
        () => ({
            lg: company?.isInstantApprovalStatsEnabled ? 4 : 6,
            md: 12,
            sm: 12,
            xs: 24,
        }),
        [company?.isInstantApprovalStatsEnabled]
    );

    const dbsInstantApprovalCardValue = useMemo(() => {
        if (!company?.isInstantApprovalStatsEnabled) {
            return '';
        }

        const { instantApproved = 0, total = 0, rate = 0 } = dbsInstantApprovalStats || {};
        const percentage = (rate * 100).toFixed(0);

        return `${instantApproved} / ${total} (${percentage}%)`;
    }, [company?.isInstantApprovalStatsEnabled, dbsInstantApprovalStats]);

    return (
        <>
            <Col {...colSpan}>
                <StatisticCard
                    {...t('dashboard:statistics.statisticsBlock.applicationSubmitted', { returnObjects: true })}
                    iconType="mail"
                    value={`${noOfSubmitted}`}
                />
            </Col>
            <Col {...colSpan}>
                <StatisticCard
                    {...t('dashboard:statistics.statisticsBlock.applicationApproveded', { returnObjects: true })}
                    iconType="mail"
                    value={`${noOfApproved}`}
                />
            </Col>
            <Col {...colSpan}>
                <StatisticCard
                    {...t('dashboard:statistics.statisticsBlock.submittedToApprovedDeclined', { returnObjects: true })}
                    iconType="clock"
                    value={submittedTillApprovedOrDeclinedTime}
                />
            </Col>
            <Col {...colSpan}>
                <StatisticCard
                    {...t('dashboard:statistics.statisticsBlock.customerInfoReceivedToApprovedDeclined', {
                        returnObjects: true,
                    })}
                    iconType="clock"
                    value={customerInfoReceivedTillApprovedOrDeclinedTime}
                />
            </Col>
            {company?.isInstantApprovalStatsEnabled && (
                <Col {...colSpan}>
                    <StatisticCard
                        {...t('dashboard:statistics.statisticsBlock.dbsInstantApprovalRate', {
                            returnObjects: true,
                        })}
                        iconType="mail"
                        value={dbsInstantApprovalCardValue}
                    />
                </Col>
            )}
        </>
    );
};

const Statistics = () => {
    const { t } = useTranslation(['dashboard']);
    const { currentMonth, lastMonth } = useSeriesFields();
    const [data, setData] = useState<ApplicationCounter>(null);
    const { isLoading, generateData, error } = useApplicationCounterData();

    useEffect(() => {
        generateData(result => setData(result));
    }, [generateData, setData]);

    const { activeTabPaneKey, setActiveTabPaneKey } = useActiveTabPane();
    const onChange = useCallback(
        (key: string) => {
            setActiveTabPaneKey(key);
        },
        [setActiveTabPaneKey]
    );

    const { setVisible } = useDashboardContext();

    useEffect(() => {
        setVisible({ [StatisticsViewID]: !error });
    }, [error, setVisible]);

    if (isLoading) {
        return <LoadingElement />;
    }

    if (error) {
        return null;
    }

    const { currentMonthData, lastMonthData } = data || ({} as ApplicationCounter);

    return (
        <Col span={24}>
            <Card title={t('dashboard:statistics.title')}>
                <Tabs
                    activeKey={activeTabPaneKey}
                    items={[
                        {
                            label: currentMonth,
                            key: '1',
                            children: (
                                <StyledRow gutter={[16, 16]}>
                                    <StatisticsBlock
                                        customerInfoReceivedTillApprovedOrDeclinedTime={
                                            currentMonthData?.formattedCustomerInfoTurnAround
                                        }
                                        dbsInstantApprovalStats={currentMonthData?.dbsInstantApprovalStats}
                                        noOfApproved={currentMonthData?.approved}
                                        noOfSubmitted={currentMonthData?.submitted}
                                        submittedTillApprovedOrDeclinedTime={
                                            currentMonthData?.formattedSubmittedTurnAround
                                        }
                                        t={t}
                                    />
                                </StyledRow>
                            ),
                        },
                        {
                            label: lastMonth,
                            key: '2',
                            children: (
                                <StyledRow gutter={[16, 16]}>
                                    <StatisticsBlock
                                        customerInfoReceivedTillApprovedOrDeclinedTime={
                                            lastMonthData?.formattedCustomerInfoTurnAround
                                        }
                                        dbsInstantApprovalStats={lastMonthData?.dbsInstantApprovalStats}
                                        noOfApproved={lastMonthData?.approved}
                                        noOfSubmitted={lastMonthData?.submitted}
                                        submittedTillApprovedOrDeclinedTime={
                                            lastMonthData?.formattedSubmittedTurnAround
                                        }
                                        t={t}
                                    />
                                </StyledRow>
                            ),
                        },
                    ]}
                    onChange={onChange}
                    type="card"
                />
            </Card>
        </Col>
    );
};

export default Statistics;
